import { inject, Injectable } from '@angular/core';
import { createEntityState, LoadSingle } from '../../core/state/entity.state';
import { EntityService } from '../../core/services/entity.service';
import { GenericTreeEntity } from '../generic-tree.entity';
import { Store } from '@ngxs/store';
import { ApiService } from '../../core/jsonApi/api.service';
import { firstValueFrom } from 'rxjs';
import { STATE_NAME } from '../../consumer/services/consumer.entity.service';

export const GENERIC_TREE_STATE_NAME = 'genericTrees';
export const GenericTreeEntityState = createEntityState<GenericTreeEntity>(GENERIC_TREE_STATE_NAME);

@Injectable({ providedIn: 'root' })
export class GenericTreeService extends EntityService<GenericTreeEntity> {
  override resourceType = 'generic-trees';
  override stateClass = GenericTreeEntityState;

  private _store = inject(Store);
  private _api = inject(ApiService);

  constructor() {
    super();
  }

  async refresh(id: string): Promise<void> {
    const normalized = await firstValueFrom(this._api.getDataSingle(this.resourceType, id));
    const entity = normalized[this.resourceType][id];
    this._store.dispatch(new LoadSingle(entity));
  }

  public calculateNodeLevels(entities: GenericTreeEntity[]): Map<string, number> {
    const levelMap = new Map<string, number>();
    const idMap = new Map<string, GenericTreeEntity>();
    entities.forEach(e => idMap.set(e.id, e));

    const getLevel = (node: GenericTreeEntity): number => {
      if (levelMap.has(node.id)) return levelMap.get(node.id)!;
      if (node.root || !node.parentId) {
        levelMap.set(node.id, 1);
        return 1;
      }
      const parent = idMap.get(node.parentId);
      if (!parent) {
        levelMap.set(node.id, 1);
        return 1;
      }
      const level = getLevel(parent) + 1;
      levelMap.set(node.id, level);
      return level;
    };

    entities.forEach(e => getLevel(e));
    return levelMap;
  }

  public getMetadataKeysForLevel(entities: GenericTreeEntity[], level: number, levelMap: Map<string, number>): string[] {
    const keys = new Set<string>();
    entities.forEach(node => {
      if (levelMap.get(node.id) === level && node.consumerData) {
        Object.keys(node.consumerData).forEach(k => keys.add(k));
      }
    });
    return Array.from(keys);
  }

  public findFirstNodesPerLevel(entities: GenericTreeEntity[]): Map<number, string> {
    const firstNodes = new Map<number, string>();
    const childrenMap = new Map<string, GenericTreeEntity[]>();
    const roots: GenericTreeEntity[] = [];

    // Group by parent
    entities.forEach(node => {
      if (node.root || !node.parentId) {
        roots.push(node);
      }
      else {
        const pid = node.parentId;
        if (!childrenMap.has(pid)) {
          childrenMap.set(pid, []);
        }
        childrenMap.get(pid)!.push(node);
      }
    });

    // Sort helper
    const sortFn = (a: GenericTreeEntity, b: GenericTreeEntity) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

    roots.sort(sortFn);
    childrenMap.forEach(list => list.sort(sortFn));

    // Traverse
    const visit = (node: GenericTreeEntity, level: number) => {
      if (!firstNodes.has(level)) {
        firstNodes.set(level, node.id);
      }

      const children = childrenMap.get(node.id) || [];
      children.forEach(child => visit(child, level + 1));
    };

    roots.forEach(root => visit(root, 1));

    return firstNodes;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allByQuery(query: Record<string, any> = {}) {
    const stateClass = this.stateClass;
    const sliceName = STATE_NAME;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (storeState: any): GenericTreeEntity[] => {
      const slice = storeState[sliceName];
      return stateClass.allByQuery(slice, { query });
    };
  }
}
